import React, { useEffect } from "react";
import { useFormikContext, getIn, FieldArray } from "formik";
import {
  Button,
  Label,
  FormGroup,
  Input,
  Row,
  Col,
  Container,
} from "reactstrap";
import { useEdit } from "../../Services/EditContext";

import FormInput from "../Form/FormInput";
import { FormLabel } from "../Form/FormLabel";
import FlightRow from "./FlightRow";
import useCheckMobileScreen from "../../utils/Hooks/useCheckMobileScreen";
import { ICharterRequest } from "../../Models/ICharterRequest";
import { IManifest } from "../../Models/IManifest";
import _ from "lodash";
import { IRoute } from "../../Models/IRoute";

const UseCombinedRadioButton = ({ index, id, label, onChange }: { index?: number, id: string, label: string, onChange: (p: unknown) => void }) => {
  const { setFieldValue, values } = useFormikContext<ICharterRequest>();
  const checked = getIn(values, id);

  const setValue = (value) => {
    onChange ? onChange(value) : setFieldValue(id, value);
  };
  return (
    <FormGroup tag="fieldset" row>
      <legend className="col-form-label col-sm-2">{label}</legend>
      <Col sm={10}>
        <FormGroup check>
          <Label check>
            <Input
              type="radio"
              name={"radioId" + index}
              onChange={() => setValue(!checked)}
              checked={checked}
            />{" "}
            Same for each Route
          </Label>
        </FormGroup>
        <FormGroup check>
          <Label check>
            <Input
              type="radio"
              name={"radioId" + index}
              onChange={() => setValue(!checked)}
              checked={!checked}
            />{" "}
            Different for each Route
          </Label>
        </FormGroup>
      </Col>
    </FormGroup>
  );
};

const newManifest = (order = 0) => {
  const newMan = {
    passengerName: "",
    contactNumber: "",
    emailAddress: "",
    passengerWeight: 86,
    freightWeight: 15,
    itemOrder: order + 1,
    status: "Active",
  };
  return { ...newMan };
};

const ManifestRow = ({ index, manIndex, push, remove }: { index: number, manIndex: number, push: (man: ReturnType<typeof newManifest>) => void, remove: (i: number) => void, placeBid?: boolean }) => {
  const { values } = useFormikContext<ICharterRequest>();
  const [edit] = useEdit();
  const isMobile = useCheckMobileScreen();

  const man = values.routes[index].manifest;
  const manifest = man[manIndex] ?? {};
  const anyShared = man.reduce((a, b) => {
    return a ? a : b.share;
  }, false);
  const showLabel = manIndex === 0 || isMobile;
  return (
    <Container
      style={{ backgroundColor: index % 2 === 1 ? "transparent" : "#ebecfaSs" }}
    >
      {anyShared && manIndex === 0 ? (
        <h3>Cannot change this manifest, as the Charter has been Shared</h3>
      ) : null}

      <Row>
        <Col xs="12" sm="7">
          <Row>
            <Col xs="4" style={{ paddingLeft: 7, paddingRight: 7 }}>
              <FormInput
                noEdit={anyShared}
                id={`routes[${index}].manifest[${manIndex}].passengerName`}
                label={showLabel ? "Passenger" : undefined}
              />
            </Col>
            <Col xs="4" style={{ paddingLeft: 0, paddingRight: 7 }}>
              <FormInput
                noEdit={anyShared}
                id={`routes[${index}].manifest[${manIndex}].contactNumber`}
                label={showLabel ? "Phone" : undefined}
              />
            </Col>
            <Col xs="4" style={{ paddingLeft: 0, paddingRight: 7 }}>
              <FormInput
                noEdit={anyShared}
                id={`routes[${index}].manifest[${manIndex}].emailAddress`}
                label={showLabel ? "Email" : undefined}
              />
            </Col>
          </Row>
        </Col>
        <Col xs="12" sm="5">
          <Row>
            <Col xs="4" style={{ paddingLeft: 0, paddingRight: 7 }}>
              <FormInput
                noEdit={anyShared}
                type={"number"}
                id={`routes[${index}].manifest[${manIndex}].passengerWeight`}
                label={showLabel ? "Pax (kg)" : undefined}
                placeholder="Kg"
              />
            </Col>
            <Col xs="3" style={{ paddingLeft: 0, paddingRight: 7 }}>
              <FormInput
                noEdit={anyShared}
                type={"number"}
                id={`routes[${index}].manifest[${manIndex}].freightWeight`}
                label={showLabel ? "Bag (kg)" : undefined}
                placeholder="Kg"
              />
            </Col>
            <Col xs="3" style={{ paddingLeft: 0, paddingRight: 7 }}>
              <FormInput
                noEdit={anyShared}
                type={"number"}
                id={`routes[${index}].manifest[${manIndex}].cargoWeight`}
                label={showLabel ? "Cargo" : undefined}
                placeholder="Kg"
              />
            </Col>
            <Col xs="2" style={{ paddingLeft: 7, paddingRight: 0 }}>
              {showLabel && <FormLabel> </FormLabel>}
              <Row style={{ paddingLeft: 1, height: 15 }}>
                {edit && man && man.length > 1 ? (
                  manifest.share ? (
                    "Shared"
                  ) : (
                    <Button className="link" onClick={() => remove(manIndex)}>
                      remove
                    </Button>
                  )
                ) : manifest.share ? (
                  "Shared"
                ) : null}
              </Row>
              <Row style={{ paddingLeft: 1, paddingTop: 5 }}>
                {edit && !anyShared && manIndex === man.length - 1 ? (
                  <Button className="link" onClick={() => push(newManifest())}>
                    add
                  </Button>
                ) : null}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};
const ManifestItem = ({ placeBid, index }: { placeBid?: boolean, index: number, anyShared?: boolean, setEditManifest?: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { values } = useFormikContext<ICharterRequest>();
  const route = values.routes[index];
  const [edit] = useEdit();

  return !edit && route.manifest.length === 0 ? (
    <h6>No passengers listed</h6>
  ) : (
    <FieldArray
      name={`routes[${index}].manifest`}
      render={({ push, remove }) => {
        if (edit && (!route.manifest || route.manifest.length === 0)) {
          push(newManifest());
        }
        return route && route.manifest ? (
          <React.Fragment>
            {index === 0 ? <h6>
              Please complete this list at least 24 hours before the first
              flight.
            </h6> : null}
            {route.manifest?.map((x, i) => (
              <ManifestRow
                key={i}
                placeBid={placeBid}
                remove={remove}
                push={push}
                index={index}
                manIndex={i}
              />
            ))}
          </React.Fragment>
        ) : null;
      }}
    />
  );
};

/**
 * Given two passengers, determine whether they're the same person or not
 */
const passengersEqual = (a: IManifest, b: IManifest) => a.passengerName === b.passengerName &&
  a.contactNumber === b.contactNumber &&
  a.emailAddress === b.emailAddress;

const Manifest = ({ placeBid, setEditManifest }: { placeBid?: true, setEditManifest?: React.Dispatch<React.SetStateAction<boolean>>, newRequest?: true }) => {
  const { setFieldValue, values } = useFormikContext<ICharterRequest>();
  const routes = values.routes;
  const shareManifest = values.shareManifest;
  const [edit] = useEdit();
  const setShare = (v) => {
    setFieldValue("shareManifest", v);
    if (v) {
      routes.map((x, i) => {
        return (
          i &&
          setFieldValue(`routes[${i}].manifest`, !v ? routes[0].manifest : [])
        );
      });
    }
  };
  const anyShared = routes?.reduce(
    (a, b) => (a ? a : b.manifest?.reduce((a, b) => (a ? a : b.share), false)),
    false
  );
  useEffect(() => {
    if (shareManifest && anyShared) {
      setFieldValue("shareManifest", false);
    }
  }, [anyShared, shareManifest, setFieldValue]);

  // A summary of all passengers from all routes
  const passengers = (values.routes as Partial<IRoute>[])
    .reduce((acc, currRoute) => {
      return [...acc, ...currRoute.manifest ?? []]
    }
      , [] as IManifest[])
    .filter(p => p.id);

  return (
    <React.Fragment>
      {!anyShared && edit && routes.length > 1 ? (
        <UseCombinedRadioButton
          id="shareManifest"
          label="Passenger Details are:"
          onChange={setShare}
        />
      ) : null}
      {passengers.length > 0 && <Button onClick={() => {
        const csvContent = passengers.reduce((acc, passenger) =>
          acc.concat(
            `${passenger.passengerName.replaceAll(',', ' ')}\
            ,${passenger.contactNumber}\
            ,${passenger.emailAddress.replaceAll(',', ' ')}\
            ,${passenger.passengerWeight}\
            ,${passenger.freightWeight}\
            ,${passenger.cargoWeight}\
            ,${routes.findIndex(route => route.id === passenger.routesId) + 1}\n`
          )
          , "Passenger Name,Passenger Phone Number,Passenger Email,Passenger Weight,Bag Weight,Cargo,Sector Number\n");
        const file = new Blob([csvContent], { type: "text/csv" });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(file);
        a.download = `JB${values.jobReference}_manifest.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }}>Export passenger list</Button>}
      {shareManifest ? (
        <ManifestItem
          anyShared={anyShared}
          placeBid={placeBid}
          index={0}
          setEditManifest={setEditManifest}
        />
      ) : (
        routes.map((_, i) => (
          <React.Fragment key={i}>
            <FlightRow noEdit index={i} />
            <ManifestItem
              placeBid={placeBid}
              index={i}
              setEditManifest={setEditManifest}
            />
          </React.Fragment>
        ))
      )}
    </React.Fragment>
  );
};
export default Manifest;
